import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { $patchStyleText } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type EditorState,
  type LexicalEditor,
  type TextFormatType,
} from "lexical";
import { Bold, Highlighter, Italic, Underline } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { htmlStyleImport } from "@/components/htmlStyleImport";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const BACKGROUND_COLORS = [
  "#fff59d",
  "#ffcc80",
  "#ef9a9a",
  "#a5d6a7",
  "#90caf9",
  "#ce93d8",
  "#ffffff",
] as const;

const editorTheme = {
  paragraph: "m-0",
  text: {
    bold: "font-bold",
    italic: "italic",
    underline: "underline",
  },
};

function onError(error: Error) {
  console.error(error);
}

function isProbablyHtml(value: string): boolean {
  return /<[a-z][\s\S]*>/i.test(value);
}

function $setPlainText(value: string) {
  const root = $getRoot();
  root.clear();
  const lines = value.replace(/\r\n/g, "\n").split("\n");
  for (const line of lines) {
    root.append($createParagraphNode().append($createTextNode(line)));
  }
  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }
}

function setEditorHtml({
  editor,
  html,
}: {
  editor: LexicalEditor;
  html: string;
}) {
  editor.update(
    () => {
      const root = $getRoot();
      const trimmed = html.trim();
      if (!trimmed) {
        root.clear();
        root.append($createParagraphNode());
        return;
      }

      if (!isProbablyHtml(trimmed)) {
        $setPlainText(html);
        return;
      }

      root.clear();
      const dom = new DOMParser().parseFromString(trimmed, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      if (nodes.length === 0) {
        $setPlainText(html);
        return;
      }

      const hasBlock = nodes.some(
        (node) => $isElementNode(node) && !node.isInline(),
      );
      if (!hasBlock) {
        root.append($createParagraphNode());
      }
      root.selectEnd();
      $insertNodes(nodes);
    },
    { tag: "historic" },
  );
}

function getEditorHtml(editor: LexicalEditor): string {
  return editor.getEditorState().read(() => $generateHtmlFromNodes(editor));
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [formats, setFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [showColors, setShowColors] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      setFormats({ bold: false, italic: false, underline: false });
      return;
    }
    setFormats({
      bold: selection.hasFormat("bold"),
      italic: selection.hasFormat("italic"),
      underline: selection.hasFormat("underline"),
    });
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateToolbar]);

  const toggleFormat = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const applyBackground = (color: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      $patchStyleText(selection, {
        "background-color": color === "#ffffff" ? null : color,
      });
    });
    setShowColors(false);
  };

  return (
    <div className="bg-muted/40 flex flex-wrap items-center gap-0.5 border-b px-1 py-1">
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Bold"
        aria-pressed={formats.bold}
        className={cn(formats.bold && "bg-accent")}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => toggleFormat("bold")}
      >
        <Bold />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Italic"
        aria-pressed={formats.italic}
        className={cn(formats.italic && "bg-accent")}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => toggleFormat("italic")}
      >
        <Italic />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Underline"
        aria-pressed={formats.underline}
        className={cn(formats.underline && "bg-accent")}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => toggleFormat("underline")}
      >
        <Underline />
      </Button>
      <div className="relative">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label="Background color"
          aria-expanded={showColors}
          className={cn(showColors && "bg-accent")}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowColors((open) => !open)}
        >
          <Highlighter />
        </Button>
        {showColors ? (
          <div className="bg-background border-border absolute top-full left-0 z-10 mt-1 flex gap-1 rounded-md border p-1 shadow-md">
            {BACKGROUND_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={`Background ${color}`}
                className="border-border size-5 rounded-sm border"
                style={{ backgroundColor: color }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyBackground(color)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HtmlValuePlugin({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const lastEmittedRef = useRef<string | null>(null);
  const readyRef = useRef(false);
  const skipChangeRef = useRef(false);

  useEffect(() => {
    const applyValue = (next: string) => {
      skipChangeRef.current = true;
      setEditorHtml({ editor, html: next });
      lastEmittedRef.current = next;
    };

    if (!readyRef.current) {
      applyValue(value);
      readyRef.current = true;
      return;
    }
    if (value === lastEmittedRef.current) return;
    const current = getEditorHtml(editor);
    if (value === current) {
      lastEmittedRef.current = value;
      return;
    }
    applyValue(value);
  }, [editor, value]);

  const handleChange = useCallback(
    (_editorState: EditorState, nextEditor: LexicalEditor) => {
      if (!readyRef.current) return;
      if (skipChangeRef.current) {
        skipChangeRef.current = false;
        return;
      }
      const html = getEditorHtml(nextEditor);
      if (html === lastEmittedRef.current) return;
      lastEmittedRef.current = html;
      onChange(html);
    },
    [onChange],
  );

  return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />;
}

export function HtmlFieldEditor({
  id,
  value,
  onChange,
  onBlur,
  collapseWhenIdle = false,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  /** Stay a short preview until the field is focused. */
  collapseWhenIdle?: boolean;
}) {
  const [active, setActive] = useState(false);
  const expanded = !collapseWhenIdle || active;
  const initialConfig = useMemo(
    () => ({
      namespace: "HtmlFieldEditor",
      theme: editorTheme,
      onError,
      html: {
        import: htmlStyleImport,
      },
    }),
    [],
  );

  return (
    <div
      id={id}
      className="html-field-editor border-input bg-background w-full overflow-hidden rounded-md border"
      onFocus={() => {
        if (collapseWhenIdle) setActive(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          if (collapseWhenIdle) setActive(false);
          onBlur?.();
        }
      }}
    >
      <LexicalComposer initialConfig={initialConfig}>
        {expanded ? <ToolbarPlugin /> : null}
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              aria-label="HTML editor"
              aria-expanded={expanded}
              className={cn(
                "px-3 py-2 text-sm outline-none [&_p]:m-0",
                expanded
                  ? "min-h-[200px]"
                  : "h-9 max-h-9 min-h-9 cursor-text overflow-hidden py-1 whitespace-nowrap [&_*]:inline [&_br]:hidden [&_img]:hidden [&_p]:inline",
              )}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <HtmlValuePlugin value={value} onChange={onChange} />
      </LexicalComposer>
    </div>
  );
}
